import { useState, useEffect, useCallback } from 'react'
import { useDebounce } from 'use-debounce'
import { Filters, SEARCH_DEBOUNCE_MS } from '../constants'

interface SearchState {
  searchTerm: string
  setSearchTerm: (term: string) => void
  debouncedSearchTerm: string
  selectedTab: 'firstbite' | 'unmatched' | 'firstbitedoors' | 'unmatcheddoors'
  setSelectedTab: (
    tab: 'firstbite' | 'unmatched' | 'firstbitedoors' | 'unmatcheddoors'
  ) => void
  filters: Filters
  setFilters: React.Dispatch<React.SetStateAction<Filters>>
  sortBy: string
  setSortBy: (sort: string) => void
  firstBitePage: number
  setFirstBitePage: (page: number) => void
  unmatchedPage: number
  setUnmatchedPage: (page: number) => void
  firstBiteDoorPage: number
  setFirstBiteDoorPage: (page: number) => void
  unmatchedDoorPage: number
  setUnmatchedDoorPage: (page: number) => void
  currentPage: number
  setCurrentPage: (page: number) => void
  resetPagination: () => void
}

interface UseUnifiedSearchStateProps {
  initialState: {
    searchTerm: string
    selectedTab: 'firstbite' | 'unmatched' | 'firstbitedoors' | 'unmatcheddoors'
    filters: Filters
    sortBy: string
    firstBitePage: number
    unmatchedPage: number
    firstBiteDoorPage: number
    unmatchedDoorPage: number
  }
}

export function useUnifiedSearchState({
  initialState,
}: UseUnifiedSearchStateProps): SearchState {
  // Search state
  const [searchTerm, setSearchTerm] = useState(initialState.searchTerm)
  const [debouncedSearchTerm] = useDebounce(searchTerm, SEARCH_DEBOUNCE_MS)

  // Tab state
  const [selectedTab, setSelectedTab] = useState<
    'firstbite' | 'unmatched' | 'firstbitedoors' | 'unmatcheddoors'
  >(initialState.selectedTab)

  // Filter and sort state
  const [filters, setFilters] = useState<Filters>(initialState.filters)
  const [sortBy, setSortBy] = useState(initialState.sortBy)

  // Pagination state
  const [firstBitePage, setFirstBitePage] = useState(initialState.firstBitePage)
  const [unmatchedPage, setUnmatchedPage] = useState(initialState.unmatchedPage)
  const [firstBiteDoorPage, setFirstBiteDoorPage] = useState(
    initialState.firstBiteDoorPage
  )
  const [unmatchedDoorPage, setUnmatchedDoorPage] = useState(
    initialState.unmatchedDoorPage
  )

  // Derived state
  const currentPage =
    selectedTab === 'firstbite'
      ? firstBitePage
      : selectedTab === 'firstbitedoors'
        ? firstBiteDoorPage
        : selectedTab === 'unmatcheddoors'
          ? unmatchedDoorPage
          : unmatchedPage
  const setCurrentPage =
    selectedTab === 'firstbite'
      ? setFirstBitePage
      : selectedTab === 'firstbitedoors'
        ? setFirstBiteDoorPage
        : selectedTab === 'unmatcheddoors'
          ? setUnmatchedDoorPage
          : setUnmatchedPage

  // Reset pagination when search term changes
  const resetPagination = useCallback(() => {
    setFirstBitePage(1)
    setUnmatchedPage(1)
    setFirstBiteDoorPage(1)
    setUnmatchedDoorPage(1)
  }, [])

  useEffect(() => {
    resetPagination()
  }, [debouncedSearchTerm, resetPagination])

  // Update state when initial state changes (from URL params)
  useEffect(() => {
    setSearchTerm(initialState.searchTerm)
    setSelectedTab(initialState.selectedTab)
    setFilters(initialState.filters)
    setSortBy(initialState.sortBy)
    setFirstBitePage(initialState.firstBitePage)
    setUnmatchedPage(initialState.unmatchedPage)
    setFirstBiteDoorPage(initialState.firstBiteDoorPage)
    setUnmatchedDoorPage(initialState.unmatchedDoorPage)
  }, [initialState])

  return {
    searchTerm,
    setSearchTerm,
    debouncedSearchTerm,
    selectedTab,
    setSelectedTab,
    filters,
    setFilters,
    sortBy,
    setSortBy,
    firstBitePage,
    setFirstBitePage,
    unmatchedPage,
    setUnmatchedPage,
    firstBiteDoorPage,
    setFirstBiteDoorPage,
    unmatchedDoorPage,
    setUnmatchedDoorPage,
    currentPage,
    setCurrentPage,
    resetPagination,
  }
}
